import { InputAdornment, SxProps, TextField } from "@mui/material";
import classNames from "classnames";
import { ReactNode } from "react";
import { useValidationBlur } from "../../hooks/use-validation-blur";

export type OutlinedInputProps<T = string> = {
  label?: string;
  name?: string;
  value?: T;
  onChange: (value: T) => void;
  className?: string;
  type?: React.HTMLInputTypeAttribute;
  multiline?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  startDecorator?: React.ReactNode;
  endDecorator?: React.ReactNode;
  sx?: SxProps;
  size?: "small" | "medium";
  error?: string;
  required?: boolean;
  forceValidate?: boolean;
};

export const OutlinedInput = <T,>({
  label,
  name,
  value = "" as T,
  onChange,
  className,
  type = "text",
  multiline = false,
  helperText,
  disabled = false,
  startDecorator,
  endDecorator,
  sx,
  size = "small",
  error,
  required,
  forceValidate,
}: OutlinedInputProps<T>) => {
  const { shouldValidate, onBlur } = useValidationBlur();
  const showError = !!error && (shouldValidate || !!forceValidate);

  const adornments = {
    startAdornment: null,
    endAdornment: null,
  } as {
    startAdornment: React.ReactNode | null;
    endAdornment: React.ReactNode | null;
  };

  if (startDecorator) {
    adornments.startAdornment = (
      <InputAdornment position="start">{startDecorator}</InputAdornment>
    );
  }

  if (endDecorator) {
    adornments.endAdornment = (
      <InputAdornment position="end">{endDecorator}</InputAdornment>
    );
  }

  return (
    <TextField
      type={type}
      size={size}
      variant="outlined"
      fullWidth
      label={label}
      name={name}
      required={required}
      onBlur={onBlur}
      className={classNames(className, "outlined-input")}
      value={value}
      onChange={(e) => onChange(e.target.value as T)}
      sx={sx}
      multiline={multiline}
      minRows={multiline ? 3 : 1}
      helperText={showError ? error : helperText}
      disabled={disabled}
      error={showError}
      slotProps={{
        input: adornments,
      }}
    />
  );
};
